import { Button, Box, Typography, Tooltip, Divider, Skeleton } from "@mui/material";
import { theme } from "@zesty-io/material";
import { useTranslation } from "react-i18next";
import { AddRounded } from "@mui/icons-material";
import { useGetAuditsQuery, useGetContentModelQuery, useGetInstanceSettingsQuery } from "../../../../../../../../shell/services/instance";
import { useHistory } from "react-router";
import { useState } from "react";
import { CreateContentItemDialog } from "../../../../../../../../shell/components/CreateContentItemDialog";
import { AnalyticsDateFilter } from "../../components/AnalyticsDateFilter";
import { useSelector } from "react-redux";
import { AppState } from "../../../../../../../../shell/store/types";
import { Metric } from "../../components/Metric";
import { useGetAnalyticsPropertyDataByQueryQuery } from "../../../../../../../../shell/services/analytics";
import { convertSecondsToMinutesAndSeconds, findValuesForDimensions, generateDateRangesForReport, getDateRangeAndLabelsFromParams } from "../../utils";
import { ByDayLineChart } from "./ByDayLineChart";
import { useParams as useQueryParams } from "../../../../../../../../shell/hooks/useParams";
import { UsersDoughnutChart } from "./UsersDoughutChart";
import { AnalyticsPropertySelector } from "../../components/AnalyticsPropertySelector";
import { ItemsTable } from "./ItemsTable/ItemsTable";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import instanceZUID from "../../../../../../../../utility/instanceZUID";
import { NotFound } from "../../../../../../../../shell/components/NotFound";
// date-fns
import { subMonths } from "date-fns";
type Props = {
  loading: boolean;
};
const AnalyticsDashboard = ({
  loading
}: Props) => {
  const {
    t
  } = useTranslation();
  const [params] = useQueryParams();
  const instance = useSelector((state: AppState) => state.instance);
  const {
    data: instanceSettings,
    isFetching: instanceSettingsFetching
  } = useGetInstanceSettingsQuery();
  const propertyId = instanceSettings?.find(setting => setting.key === "google_property_id")?.value;
  const [startDate, endDate, dateRange0Label, dateRange1Label] = getDateRangeAndLabelsFromParams(params);
  const {
    data: ga4Data,
    isFetching,
    isError,
    refetch
  } = useGetAnalyticsPropertyDataByQueryQuery({
    property: propertyId,
    requests: [{
      // overall sessions, duration, bounce and conversions
      metrics: [{
        name: "sessions"
      }, {
        name: "averageSessionDuration"
      }, {
        name: "bounceRate"
      }, {
        name: "eventCount"
      }, {
        name: "conversions"
      }],
      dateRanges: generateDateRangesForReport(startDate, endDate)
    }, {
      // daily and total sessions
      dimensions: [{
        name: "date"
      }],
      metrics: [{
        name: "sessions"
      }],
      dateRanges: generateDateRangesForReport(startDate, endDate),
      orderBys: [{
        dimension: {
          dimensionName: "date"
        }
      }]
    }, {
      // user traffic default channel
      dimensions: [{
        name: "firstUserDefaultChannelGroup"
      }],
      metrics: [{
        name: "sessions"
      }],
      dateRanges: generateDateRangesForReport(startDate, endDate),
      metricAggregations: ["TOTAL"]
    }, {
      // new vs returning
      dimensions: [{
        name: "newVsReturning"
      }],
      metrics: [{
        name: "totalUsers"
      }],
      dateRanges: generateDateRangesForReport(startDate, endDate)
    }]
  }, {
    skip: !propertyId
  });
  const [metricsReport, dailySessionsReport, userSourcesReport, newVsReturningReport] = ga4Data?.reports || [];
  const isLoading = isFetching || instanceSettingsFetching || loading;
  if (isError) {
    return <NotFound title={t("content.analyticsUnableToLoadTitle")} message={t("content.analyticsUnableToLoadMessage")} button={<>
            <Button startIcon={<SupportAgentRoundedIcon color="action" />} variant="contained" color="inherit" sx={{
        mr: 2
      }} onClick={() => window.open(`https://www.zesty.io/instances/${instanceZUID}/support/`)}>
              {t("content.analyticsContactSupport")}
            </Button>
            <Button startIcon={<RefreshRoundedIcon />} variant="contained" onClick={() => refetch()}>
              {t("content.analyticsTryAgain")}
            </Button>
          </>} />;
  }
  return <Box color={theme.palette.text.primary} bgcolor={theme.palette.grey[50]} boxSizing="border-box" display="flex" flexDirection="column" height="100%" data-sentry-element="Box" data-sentry-component="AnalyticsDashboard" data-sentry-source-file="index.tsx">
      <AnalyticsDashboardHeader data-sentry-element="AnalyticsDashboardHeader" data-sentry-source-file="index.tsx" />
      <Box pb={8} px={4} overflow="auto" height="100%" data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Box display="flex" py={2} justifyContent="space-between" data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <AnalyticsDateFilter data-sentry-element="AnalyticsDateFilter" data-sentry-source-file="index.tsx" />
          <Typography variant="h6" fontWeight="600" maxWidth={304} noWrap data-sentry-element="Typography" data-sentry-source-file="index.tsx">
            {instance.name}
          </Typography>
          <AnalyticsPropertySelector data-sentry-element="AnalyticsPropertySelector" data-sentry-source-file="index.tsx" />
        </Box>

        <Box display="flex" gap={2} data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <Box borderRadius={"8px"} gap={2} p={2} border={theme => `1px solid ${theme.palette.border}`} bgcolor="background.paper" display="flex" width="55%" data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <Box minWidth="120px" data-sentry-element="Box" data-sentry-source-file="index.tsx">
              {isLoading ? <Skeleton variant="rectangular" width="100%" height="32px" sx={{
              bgcolor: "grey.200"
            }} /> : <>
                  <Typography variant="body2" fontWeight="600">
                    {t("content.analyticsTotalSessions")}
                  </Typography>
                  <Typography variant="body3" fontWeight="600" color="text.secondary">
                    {dateRange0Label}
                  </Typography>
                </>}
              <Box mt={2} data-sentry-element="Box" data-sentry-source-file="index.tsx">
                <Metric loading={isLoading} valueProps={{
                variant: "h3"
              }} value={+(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 0) || 0)} priorValue={+(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 0) || 0)} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
              </Box>
            </Box>

            <ByDayLineChart startDate={startDate} endDate={endDate} dateRange0Label={dateRange0Label} dateRange1Label={dateRange1Label} data={dailySessionsReport} loading={isLoading} data-sentry-element="ByDayLineChart" data-sentry-source-file="index.tsx" />
          </Box>

          <Box borderRadius={"8px"} gap={2} p={2} border={theme => `1px solid ${theme.palette.border}`} bgcolor="background.paper" display="flex" width="55%" data-sentry-element="Box" data-sentry-source-file="index.tsx">
            <UsersDoughnutChart startDate={startDate} endDate={endDate} dateRange0Label={dateRange0Label} dateRange1Label={dateRange1Label} usersData={newVsReturningReport} sourceData={userSourcesReport} loading={isLoading} data-sentry-element="UsersDoughnutChart" data-sentry-source-file="index.tsx" />
          </Box>
        </Box>

        <Box display="flex" justifyContent={"space-between"} borderRadius={"8px"} gap={2} p={2} mt={2} bgcolor="background.paper" border={theme => `1px solid ${theme.palette.border}`} data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <Metric loading={isLoading} title={t("content.analyticsMetricSessions")} value={+(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 0) || 0)} priorValue={+(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 0) || 0)} description={t("content.analyticsMetricSessionsDesc")} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
          <Divider orientation="vertical" flexItem data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
          <Metric loading={isLoading} title={t("content.analyticsMetricAvgDuration")} formatter={convertSecondsToMinutesAndSeconds} value={+(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 1) || 0)} priorValue={+(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 1) || 0)} description={t("content.analyticsMetricAvgDurationDesc")} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
          <Divider orientation="vertical" flexItem data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
          <Metric loading={isLoading} inverse title={t("content.analyticsMetricBounceRate")} formatter={(value: number) => `${Math.floor(value * 100)}%`} value={+(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 2) || 0)} priorValue={+(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 2) || 0)} description={t("content.analyticsMetricBounceRateDesc")} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
          <Divider orientation="vertical" flexItem data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
          <Metric loading={isLoading} title={t("content.analyticsMetricEvents")} value={+(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 3) || 0)} priorValue={+(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 3) || 0)} description={t("content.analyticsMetricConversionsDesc")} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
          <Divider orientation="vertical" flexItem data-sentry-element="Divider" data-sentry-source-file="index.tsx" />
          <Metric loading={isLoading} title={t("content.analyticsMetricConversions")} value={+(findValuesForDimensions(metricsReport?.rows, ["date_range_0"], 4) || 0)} priorValue={+(findValuesForDimensions(metricsReport?.rows, ["date_range_1"], 4) || 0)} description={t("content.analyticsMetricConversionsDesc")} data-sentry-element="Metric" data-sentry-source-file="index.tsx" />
        </Box>

        <Box mt={2} data-sentry-element="Box" data-sentry-source-file="index.tsx">
          <ItemsTable propertyId={propertyId} startDate={startDate} endDate={endDate} data-sentry-element="ItemsTable" data-sentry-source-file="index.tsx" />
        </Box>
      </Box>
    </Box>;
};
export default AnalyticsDashboard;
const AnalyticsDashboardHeader = () => {
  const {
    t
  } = useTranslation();
  const now = new Date();
  const endDateUtc = now.toISOString().slice(0, 10);
  const startDateUtc = subMonths(now, 1).toISOString().slice(0, 10);
  const {
    data: audit
  } = useGetAuditsQuery({
    start_date: startDateUtc,
    end_date: endDateUtc
  });
  const [showCreateContentItemDialog, setShowCreateContentItemDialog] = useState(false);
  const usedModelsCounts = audit?.filter((a: any) => a.resourceType === "content" && a.action === 1)?.map((a: any) => a?.meta?.uri?.split("/")[4]).reduce((acc: Record<string, number>, value: string) => {
    if (value in acc) acc[value] += 1;else acc[value] = 1;
    return acc;
  }, {} as Record<string, number>);
  const topUsedModels = Object.entries(usedModelsCounts || {})?.sort((a: any, b: any) => b[1] - a[1])?.slice(0, 4);
  return <>
      <Box display="flex" justifyContent="space-between" px={4} pt={4} pb={1.75} borderBottom={`2px solid ${theme.palette.border}`} bgcolor={theme.palette.background.paper} data-sentry-element="Box" data-sentry-source-file="index.tsx">
        <Typography variant="h3" fontWeight="700" data-sentry-element="Typography" data-sentry-source-file="index.tsx">
          {t("content.analyticsDashboard")}
        </Typography>
        <Box display="flex" gap={1.5} height="fit-content" data-sentry-element="Box" data-sentry-source-file="index.tsx">
          {topUsedModels?.reverse()?.map((model: any) => <CreateItemButton key={model[0]} modelZUID={model[0]} />)}
          <Button variant="contained" size="small" startIcon={<AddRounded />} onClick={() => setShowCreateContentItemDialog(true)} data-sentry-element="Button" data-sentry-source-file="index.tsx">
            {t("common.create")}
          </Button>
        </Box>
      </Box>
      {showCreateContentItemDialog && <CreateContentItemDialog open onClose={() => setShowCreateContentItemDialog(false)} />}
    </>;
};
const CreateItemButton = ({
  modelZUID
}: {
  modelZUID: string;
}) => {
  const {
    data: model,
    isError
  } = useGetContentModelQuery(modelZUID);
  const history = useHistory();
  return <Button sx={{
    display: isError && "none"
  }} variant="outlined" size="small" color="inherit" startIcon={<AddRounded color="action" />} onClick={() => history.push(`/content/${model.ZUID}/new`)} data-sentry-element="Button" data-sentry-component="CreateItemButton" data-sentry-source-file="index.tsx">
      <Tooltip title={model?.label} placement="bottom" arrow enterDelay={1000} data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
        <Typography sx={{
        maxWidth: "74px"
      }} variant="body2" noWrap data-sentry-element="Typography" data-sentry-source-file="index.tsx">
          {model?.label}
        </Typography>
      </Tooltip>
    </Button>;
};